// Kalender panel "Input Rotasi Cepat" -- fungsi murni, tanpa dependensi. Tanggal = teks "YYYY-MM-DD"
// (dibandingkan sebagai teks, sama dgn backend). Aturan sebenarnya ditegakkan SERVER (rotasi_cepat.py):
// di sini hanya cermin supaya pengguna langsung diberi tahu sebelum menyimpan.
import { BLOK_JAGA, BLOK_STASE_LUAR, BLOK_STASE_RSCM, LABEL_BLOK, barisBaru } from "@/lib/rotasiCepat";

export const NAMA_HARI = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];
export const NAMA_BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus",
    "September", "Oktober", "November", "Desember"];
const BLOK_STASE = [BLOK_STASE_RSCM, BLOK_STASE_LUAR];

const dua = (n) => String(n).padStart(2, "0");
export const isoTanggal = (tahun, bulan, hari) => `${tahun}-${dua(bulan)}-${dua(hari)}`;
const jumlahHariBulan = (tahun, bulan) => new Date(Number(tahun), Number(bulan), 0).getDate();
const bulanDari = (iso) => [Number(iso.slice(5, 7)), Number(iso.slice(0, 4))];

export function batasBulanIso(bulan, tahun) {
    return [isoTanggal(tahun, bulan, 1), isoTanggal(tahun, bulan, jumlahHariBulan(tahun, bulan))];
}

// Minggu-minggu (Senin pertama) berisi teks tanggal, null = sel kosong di luar bulan.
export function gridBulan(tahun, bulan) {
    const geser = (new Date(Number(tahun), Number(bulan) - 1, 1).getDay() + 6) % 7;
    const sel = [...Array(geser).fill(null)];
    for (let h = 1; h <= jumlahHariBulan(tahun, bulan); h += 1) sel.push(isoTanggal(tahun, bulan, h));
    while (sel.length % 7) sel.push(null);
    return Array.from({ length: sel.length / 7 }, (_, i) => sel.slice(i * 7, i * 7 + 7));
}

export function geserBulan(bulan, tahun, n) {
    const i = Number(tahun) * 12 + Number(bulan) - 1 + n;
    return { bulan: (i % 12) + 1, tahun: Math.floor(i / 12) };
}

const tambahHari = (iso, n) => {
    const [t, b, h] = iso.split("-").map(Number);
    const d = new Date(Date.UTC(t, b - 1, h + n));
    return d.toISOString().slice(0, 10);
};

export function hariDalamRentang(mulai, akhir) {
    const hasil = [];
    for (let d = mulai; d && akhir && d <= akhir; d = tambahHari(d, 1)) hasil.push(d);
    return hasil;
}

export function hariDiBulan(mulai, akhir, bulan, tahun) {
    const [awal, akhirBulan] = batasBulanIso(bulan, tahun);
    return hariDalamRentang(mulai > awal ? mulai : awal, akhir < akhirBulan ? akhir : akhirBulan).length;
}

// Klik tanggal di mode Stase: klik pertama = awal, klik kedua = akhir (ditukar bila lebih awal),
// klik berikutnya memulai pilihan baru.
export function pilihRentang(pilihan, tanggal) {
    if (!pilihan?.awal || pilihan.akhir) return { awal: tanggal, akhir: null };
    return tanggal < pilihan.awal ? { awal: tanggal, akhir: pilihan.awal } : { awal: pilihan.awal, akhir: tanggal };
}

// Cermin periksa_baris (server): rentang wajib menyentuh bulan panel; lintas bulan hanya di dalam batas
// dari server (aturan.batas_lintas_bulan). Teks penolakan lintas bulan = aturan.pesan_lintas_bulan dari
// server (sama persis dgn pesan server). null = lolos.
const PESAN_LINTAS_BAWAAN = "Rentang lintas bulan tidak diizinkan untuk tanggal ini. Pisahkan rentang di bulan ini dan bulan berikutnya.";
// Cermin RC.lintas_bulan_diizinkan: rentang dalam satu bulan selalu boleh; lintas bulan hanya di dalam batas
// dari server (aturan.batas_lintas_bulan). Dipakai panel (cekRentang) & dialog Edit Peserta.
export function lintasBulanDiizinkan(mulai, akhir, batas) {
    if (mulai.slice(0, 7) === akhir.slice(0, 7)) return true;
    return !!(batas && batas.mulai <= mulai && akhir <= batas.akhir);
}

export function cekRentang(mulai, akhir, bulan, tahun, batas, pesanLintas = PESAN_LINTAS_BAWAAN) {
    const [awal, akhirBulan] = batasBulanIso(bulan, tahun);
    if (!(mulai <= akhirBulan && awal <= akhir)) return `Rentang harus menyentuh bulan ${NAMA_BULAN[bulan - 1]} ${tahun}`;
    return lintasBulanDiizinkan(mulai, akhir, batas) ? null : pesanLintas;
}

// Hari terbanyak rentang itu di salah satu bulan yang dilaluinya (utk peringatan >= ambang).
export function hariTerbanyakSebulan(mulai, akhir) {
    let [bulan, tahun] = bulanDari(mulai);
    let terbanyak = 0;
    while (isoTanggal(tahun, bulan, 1) <= akhir) {
        terbanyak = Math.max(terbanyak, hariDiBulan(mulai, akhir, bulan, tahun));
        ({ bulan, tahun } = geserBulan(bulan, tahun, 1));
    }
    return terbanyak;
}

// Tanda per tanggal: { "YYYY-MM-DD": { staseTersimpan, staseBaru, jagaTersimpan, jagaBaru } }.
// kalender = segmen tersimpan dari server ({blok, tanggal_mulai, tanggal_akhir}); form = baris baru.
export function tandaKalender(kalender, form) {
    const tanda = {};
    const tandai = (mulai, akhir, kunci) => hariDalamRentang(mulai, akhir).forEach((d) => {
        tanda[d] = { ...(tanda[d] || {}), [kunci]: true };
    });
    (kalender || []).forEach((s) => tandai(s.tanggal_mulai, s.tanggal_akhir,
        s.blok === BLOK_JAGA ? "jagaTersimpan" : "staseTersimpan"));
    [...BLOK_STASE, BLOK_JAGA].forEach((blok) => (form?.[blok] || []).forEach((b) => tandai(
        b.tanggal_mulai, b.tanggal_akhir, blok === BLOK_JAGA ? "jagaBaru" : "staseBaru")));
    return tanda;
}

// Jumlah tanggal BERBEDA di bulan itu yang terisi Stase / Jaga (tersimpan + baru).
export function hariTerisi(kalender, form, bulan, tahun) {
    const awal = isoTanggal(tahun, bulan, 1);
    const akhir = isoTanggal(tahun, bulan, jumlahHariBulan(tahun, bulan));
    const tanda = tandaKalender(kalender, form);
    const diBulan = Object.keys(tanda).filter((d) => d >= awal && d <= akhir);
    return {
        stase: diBulan.filter((d) => tanda[d].staseTersimpan || tanda[d].staseBaru).length,
        jaga: diBulan.filter((d) => tanda[d].jagaTersimpan || tanda[d].jagaBaru).length,
    };
}

// Mode Jaga: klik tanggal menyalakan/mematikan 1 baris (mulai = akhir) utk tempat yang dipilih.
// Tempat lain di tanggal yang sama tetap boleh (aturan lama); yang sama persis dimatikan.
const samaTempat = (b, tempat) => b.departemen_id === tempat.departemen_id && b.instalasi_id === tempat.instalasi_id;
export const jagaAdaDi = (form, tanggal, tempat) => form[BLOK_JAGA].some(
    (b) => b.tanggal_mulai === tanggal && samaTempat(b, tempat));

export function toggleJaga(form, tanggal, tempat) {
    if (jagaAdaDi(form, tanggal, tempat)) {
        return { ...form, [BLOK_JAGA]: form[BLOK_JAGA].filter((b) => !(b.tanggal_mulai === tanggal && samaTempat(b, tempat))) };
    }
    const baris = { ...barisBaru(), departemen_id: tempat.departemen_id, divisi_id: tempat.divisi_id || "",
        instalasi_id: tempat.instalasi_id, tanggal_mulai: tanggal, tanggal_akhir: tanggal };
    const jaga = [...form[BLOK_JAGA], baris].sort((a, b) => a.tanggal_mulai.localeCompare(b.tanggal_mulai));
    return { ...form, [BLOK_JAGA]: jaga };
}

// Mode Stase: rentang + tempat jadi 1 baris baru di blok itu.
export function tambahStase(form, blok, pilihan, tempat) {
    const akhir = pilihan.akhir || pilihan.awal;
    const baris = blok === BLOK_STASE_LUAR
        ? { ...barisBaru(), nama_rs: tempat.nama_rs.trim(), tanggal_mulai: pilihan.awal, tanggal_akhir: akhir }
        : { ...barisBaru(), departemen_id: tempat.departemen_id, divisi_id: tempat.divisi_id || "",
            instalasi_id: tempat.instalasi_id || "", tanggal_mulai: pilihan.awal, tanggal_akhir: akhir };
    return { ...form, [blok]: [...form[blok], baris] };
}

export const formKosong = () => ({ [BLOK_STASE_RSCM]: [], [BLOK_STASE_LUAR]: [], [BLOK_JAGA]: [] });

// Jaga dikelompokkan per tempat utk daftar "akan disimpan": [{kunci, departemen_id, instalasi_id, baris: [...]}]
export function kelompokJaga(form) {
    const peta = new Map();
    form[BLOK_JAGA].forEach((b) => {
        const kunci = `${b.departemen_id}|${b.instalasi_id}`;
        if (!peta.has(kunci)) peta.set(kunci, { kunci, departemen_id: b.departemen_id, instalasi_id: b.instalasi_id, baris: [] });
        peta.get(kunci).baris.push(b);
    });
    return [...peta.values()];
}

// Satu peringatan lunak dari server jadi teks: tumpang tindih sudah lengkap dari server (1 pesan per pasangan);
// >= ambang hari ditulis "<Jenis> baris N: X hari di <Bulan Tahun>.".
export const PERINGATAN_HARI_PENUH = "hari_penuh"; // = RC.PERINGATAN_HARI_PENUH (backend)
export function teksPeringatan(p) {
    if (p.jenis !== PERINGATAN_HARI_PENUH) return p.pesan;
    return `${LABEL_BLOK[p.blok]} baris ${p.indeks + 1}: ${p.hari} hari di ${NAMA_BULAN[p.bulan - 1]} ${p.tahun}.`;
}
export const pesanHariPenuh = (daftar) => (daftar || []).find((p) => p.jenis === PERINGATAN_HARI_PENUH)?.pesan || null;
